import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    useTheme,
    alpha,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { localStore } from '../utils/localStore';

interface DocumentKeyModalProps {
    open: boolean;
    onClose: () => void;
    onKeySet: (key: string) => void;
    userId: number | string;
}

const EXPIRY_DAYS = 30;

const DocumentKeyModal: React.FC<DocumentKeyModalProps> = ({
    open,
    onClose,
    onKeySet,
    userId
}) => {
    const theme = useTheme();
    const [masterKey, setMasterKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasStoredKey, setHasStoredKey] = useState(false);

    useEffect(() => {
        const checkStoredKey = async () => {
            try {
                const stored = await localStore.get(`user_${userId}_master_key`);
                const expiry = await localStore.get(`user_${userId}_master_key_expiry`);

                if (stored && expiry) {
                    const expiryDate = new Date(expiry);
                    if (expiryDate > new Date()) {
                        // Key exists and is still valid
                        setHasStoredKey(true);
                        onKeySet(stored);
                        onClose();
                        return;
                    } else {
                        // Key expired, remove it
                        await localStore.remove(`user_${userId}_master_key`);
                        await localStore.remove(`user_${userId}_master_key_expiry`);
                    }
                }
                setHasStoredKey(false);
            } catch (err) {
                console.error('Error checking stored key:', err);
                setHasStoredKey(false);
            }
        };

        if (open && userId) {
            checkStoredKey();
        }
    }, [open, userId, onKeySet, onClose]);

    const handleSubmit = async () => {
        setError(null);

        // Validate master key format
        if (!masterKey.trim()) {
            setError('Master Key is required');
            return;
        }

        try {
            const decoded = atob(masterKey);
            if (decoded.length !== 32) {
                setError('Key must be exactly 32 bytes (256-bit).');
                return;
            }
        } catch (e) {
            setError('Invalid Base64 format.');
            return;
        }

        setLoading(true);

        try {
            // Calculate expiry date (30 days from now)
            const expiryDate = new Date();
            expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);

            // Store master key and expiry
            await localStore.set(`user_${userId}_master_key`, masterKey);
            await localStore.set(`user_${userId}_master_key_expiry`, expiryDate.toISOString());

            onKeySet(masterKey);
            onClose();
        } catch (err) {
            console.error('Failed to store key:', err);
            setError('Failed to store Master Key. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        if (window.confirm("This will clear your locally stored Master Key. You'll need to re-enter it to access encrypted documents. Continue?")) {
            await localStore.remove(`user_${userId}_master_key`);
            await localStore.remove(`user_${userId}_master_key_expiry`);
            setHasStoredKey(false);
            setMasterKey('');
            setError(null);
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, px: 3, pt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <SecurityIcon sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    Document Encryption
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
                    Enter your 256-bit (Base64) Master Key. This key encrypts your documents locally.
                    The key will be valid for {EXPIRY_DAYS} days.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    autoFocus
                    label="Master Key (Base64)"
                    fullWidth
                    required
                    value={masterKey}
                    error={Boolean(error)}
                    onChange={(e) => {
                      setError(null);
                      setMasterKey(e.target.value);
                    }}
                    disabled={loading}
                    placeholder="e.g. jH9f+...=="
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                {hasStoredKey && (
                    <Button
                        color="error"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={handleReset}
                    >
                        Clear Stored Key
                    </Button>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                        boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                >
                    {loading ? 'Processing...' : 'Set Master Key'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentKeyModal;
